/**
 * Create Source Form Component
 * Modal form for adding new data sources
 */

import { useState, useCallback } from 'react';
import { createSource, type CreateSourceInput } from '../../services/adminApi';

type CreateSourceFormProps = {
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
};

const INITIAL_FORM_STATE: CreateSourceInput = {
  name: '',
  type: 'gmina',
  voivodeship: 'zachodniopomorskie',
  url: '',
  teryt_code: '',
};

export function CreateSourceForm({ onClose, onSuccess, onError }: CreateSourceFormProps) {
  const [formData, setFormData] = useState<CreateSourceInput>(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Nazwa jest wymagana';
    }

    if (!formData.url.trim()) {
      errors.url = 'URL jest wymagany';
    } else {
      try {
        new URL(formData.url);
      } catch {
        errors.url = 'Nieprawidłowy format URL';
      }
    }

    if (!formData.teryt_code.trim()) {
      errors.teryt_code = 'Kod TERYT jest wymagany';
    } else if (formData.teryt_code.length < 4 || formData.teryt_code.length > 7) {
      errors.teryt_code = 'Kod TERYT musi mieć 4-7 znaków';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createSource(formData);
      onSuccess();
      onClose();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Nie udało się dodać źródła');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, onClose, onSuccess, onError]);

  const handleChange = useCallback((field: keyof CreateSourceInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when field is edited
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [validationErrors]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dodaj nowe źródło</h3>
          <button className="modal-close" onClick={onClose} aria-label="Zamknij">
            &times;
          </button>
        </div>

        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="source-name">Nazwa *</label>
            <input
              id="source-name"
              type="text"
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="np. Gmina Szczecin"
              disabled={isSubmitting}
              className={validationErrors.name ? 'input--error' : ''}
            />
            {validationErrors.name && (
              <span className="form-error">{validationErrors.name}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="source-type">Typ *</label>
              <select
                id="source-type"
                value={formData.type}
                onChange={e => handleChange('type', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="gmina">Gmina</option>
                <option value="powiat">Powiat</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="source-voivodeship">Województwo *</label>
              <select
                id="source-voivodeship"
                value={formData.voivodeship}
                onChange={e => handleChange('voivodeship', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="zachodniopomorskie">Zachodniopomorskie</option>
                <option value="lubuskie">Lubuskie</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="source-url">URL strony z przetargami *</label>
            <input
              id="source-url"
              type="url"
              value={formData.url}
              onChange={e => handleChange('url', e.target.value)}
              placeholder="https://bip.gmina.pl/przetargi"
              disabled={isSubmitting}
              className={validationErrors.url ? 'input--error' : ''}
            />
            {validationErrors.url && (
              <span className="form-error">{validationErrors.url}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="source-teryt">Kod TERYT *</label>
            <input
              id="source-teryt"
              type="text"
              value={formData.teryt_code}
              onChange={e => handleChange('teryt_code', e.target.value)}
              placeholder="np. 3262011"
              disabled={isSubmitting}
              maxLength={7}
              className={validationErrors.teryt_code ? 'input--error' : ''}
            />
            {validationErrors.teryt_code && (
              <span className="form-error">{validationErrors.teryt_code}</span>
            )}
            <span className="form-hint">
              Kod TERYT gminy/powiatu (4-7 cyfr)
            </span>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="admin-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Anuluj
            </button>
            <button
              type="submit"
              className={`admin-button admin-button--primary ${isSubmitting ? 'admin-button--loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Dodawanie...' : 'Dodaj źródło'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
